import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useRef } from "react";

import { LayoutSuspense, usePlacement } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { navigationPlacementId, notificationCenterAction } from "../defines/navigationItems";
import { SystemMessagesContext } from "../SystemMessagesProvider";
import styles from "./DotcomHeader.module.scss";
import { DotcomHeaderDesktop } from "./DotcomHeaderDesktop";
import { DotcomHeaderLoading } from "./DotcomHeaderLoading";
import { DotcomHeaderMobile } from "./DotcomHeaderMobile";
import { DotcomHeaderMobileWithGlobalNav } from "./DotcomHeaderMobileWithGlobalNav";

const HEADER_CONTROL_PORTAL_ID = "headerControlPortal";

/**
 * DotcomHeader will render the appropriate header component base on props.size: either the primary or secondary
 * global header.
 */

export const DotcomHeader = (props) => {
  const {
    control,
    light,
    menuItemSeparator,
    onSearch,
    pageTitle,
    showLoginBanner,
    type,
    uppercaseMenuItems,
    waitForContent = true,
  } = props;
  const { placement: navTop } = usePlacement("navTop");
  const {
    placement: globalNavPlacement,
    isComplete: globalNavComplete,
    isPlacementLoading: globalNavPlacementLoading,
  } = usePlacement(navigationPlacementId);
  const {
    placement: navLogoPlacement,
    isComplete: navLogoComplete,
    isPlacementLoading: navLogoPlacementLoading,
  } = usePlacement("navLogo");
  const desktop = isDesktop();
  const headerPlacement = desktop ? globalNavPlacement : navLogoPlacement;
  const isComplete = desktop ? globalNavComplete : navLogoComplete;
  const isPlacementLoading = desktop ? globalNavPlacementLoading : navLogoPlacementLoading;
  const waitForSvgLoad = useRef(null);
  const svgLoadPromise = useMemo(
    () =>
      new Promise((resolve) => {
        waitForSvgLoad.current = resolve;
      }),
    []
  );
  const {
    addViewedMessageAdvisoryIds,
    notificationMessageAdvisoryIds,
    viewedMessageAdvisoryIds,
    shouldShowSystemMessagesInNavigation,
  } = useContext(SystemMessagesContext);
  const newNotificationCount =
    notificationMessageAdvisoryIds.filter(
      (notificationMessageAdvisoryId) =>
        !viewedMessageAdvisoryIds.includes(notificationMessageAdvisoryId)
    ).length ?? 0;

  useEffect(() => {
    const svgPath = isDesktop()
      ? headerPlacement?.globalNavLogo?.src
      : headerPlacement?.placement?.src;

    if (!isPlacementLoading && isComplete && svgPath) {
      const img = new Image();

      img.onload = waitForSvgLoad.current;
      img.src = svgPath;
    }
  }, [isPlacementLoading, isComplete]);

  return waitForContent ? (
    <div className={classNames(styles.dotcomHeader, { [styles.mobile]: !isDesktop() })}>
      <LayoutSuspense {...getLayoutSuspenseProps()}>{renderHeaderContent()}</LayoutSuspense>
    </div>
  ) : (
    renderHeaderContent()
  );

  function renderHeaderContent() {
    return isDesktop() ? <DotcomHeaderDesktop {...getDesktopProps()} /> : renderMobileHeader();
  }

  function renderHeaderFallback() {
    return (
      <DotcomHeaderLoading mobileLogoLeftAlign={shouldShowSystemMessagesInNavigation} type={type} />
    );
  }

  function renderMobileHeader() {
    return navTop !== undefined ? (
      <DotcomHeaderMobile {...getMobileProps()} />
    ) : (
      <DotcomHeaderMobileWithGlobalNav {...getMobileWithGlobalNavProps()} />
    );
  }

  function getDesktopProps() {
    return {
      control,
      light,
      menuItemSeparator,
      newNotificationCount,
      onSearch,
      onViewNotifications: handleViewNotifications,
      shouldShowSystemMessagesInNavigation,
      uppercaseMenuItems,
    };
  }

  function getLayoutSuspenseProps() {
    return {
      animate: true,
      animationToken: type,
      fallback: renderHeaderFallback(),
      waitFor: [svgLoadPromise],
    };
  }

  function getMobileProps() {
    return {
      control:
        control &&
        React.cloneElement(control, {
          headerControlPortalId: HEADER_CONTROL_PORTAL_ID,
        }),
      headerControlPortalId: HEADER_CONTROL_PORTAL_ID,
      newNotificationCount,
      notificationCenterIconName: findNotificationCenterIconName(),
      onViewNotifications: handleViewNotifications,
      pageTitle,
      shouldShowSystemMessagesInNavigation,
      showLoginBanner,
    };
  }

  function getMobileWithGlobalNavProps() {
    return {
      control,
      pageTitle,
      uppercaseMenuItems,
    };
  }

  function isDesktop() {
    return type === "desktop";
  }

  function findNotificationCenterIconName() {
    return globalNavPlacement?.globalNavPrimaryLinks?.find(
      ({ action }) => action === notificationCenterAction
    )?.prefixIcon;
  }

  function handleViewNotifications() {
    addViewedMessageAdvisoryIds(notificationMessageAdvisoryIds);
  }
};

DotcomHeader.displayName = "DotcomHeader";
DotcomHeader.propTypes = {
  /**
   * Header control component used to control items like login and locale switching
   */
  control: PropTypes.node,

  /** Indicates whether the app is using a dark Header background, to be passed to elements for styling. */
  light: PropTypes.bool,

  /** Adds the value of the prop as a separator between the menu links. */
  menuItemSeparator: PropTypes.string,

  /** Calllback that will be used when traveler performs a search. */
  onSearch: PropTypes.func,

  /** Name of current page. */
  pageTitle: PropTypes.string,

  /** Indicates whether to show the login banner */
  showLoginBanner: PropTypes.bool,

  /** <code>size</code> determines which DotcomHeader is rendered. */
  type: PropTypes.oneOf(["desktop", "mobile"]).isRequired,

  /** Transform menu item links into uppercase. Default: true */
  uppercaseMenuItems: PropTypes.bool,

  /** Whether to use the loading fallback components when waiting for placement content. */
  waitForContent: PropTypes.bool,
};
